import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { units } from '@m/alchemy-ui';
import { Challenge, Idea, Maybe, NotificationType, User } from '../../../common/core-api';
import { useFile } from '../../../hooks';
import { removeTags } from '../../../helpers';
import * as Styled from './Header.styled';
import { ChallengeLink, IdeaLink, UserLink } from '.';

export const IdeaCardHeader = ({
  type,
  notificationCount,
  challenge,
  isUserNotification,
  time,
  author,
  idea,
  sameChallenge,
  isValid
}: {
  type: NotificationType;
  notificationCount: number;
  challenge?: Maybe<Challenge> | null;
  isUserNotification: boolean;
  time?: string;
  author?: Maybe<User>;
  idea?: Maybe<Idea>;
  sameChallenge: boolean;
  isValid?: boolean;
}) => {
  const intl = useIntl();

  const [getFile] = useFile();
  const file = author?.profileImageFile;
  const url = file ? getFile({ file, width: parseInt(units(3), 2), height: parseInt(units(3), 2) }) : '';
  const challengeTitle = removeTags(challenge?.title || '');

  const target =
    type === NotificationType.IDEA_PHASE_REACHED
      ? intl.formatMessage({ id: 'phase', defaultMessage: 'phase' })
      : intl.formatMessage({ id: 'milestone', defaultMessage: 'Milestone' }).toLowerCase();

  const challengeLink = (msg: string, id: string | undefined) => (isValid ? ChallengeLink(msg, id) : msg);

  return (
    <>
      {!isUserNotification && (
        <Styled.AlignItemsWrap>
          {type === NotificationType.IDEA_NEW && challenge && (
            <FormattedMessage
              id="challengeReceivedIdeas"
              defaultMessage="The challenge <a>{challengeTitle}</a> {itemCount, plural, one {received a new idea} other {has new ideas}}"
              values={{
                challengeTitle,
                a: (msg: string) => challengeLink(msg, challenge?.id),
                itemCount: notificationCount
              }}
            />
          )}
          {type === NotificationType.IDEA_VOTE && challenge && (
            <FormattedMessage
              id="anIdeasHasNewVotes"
              defaultMessage="{itemCount, plural, one {An idea} other {Ideas}} from the challenge <a>{challengeTitle}</a> received votes"
              values={{
                challengeTitle,
                a: (msg: string) => challengeLink(msg, challenge?.id),
                itemCount: notificationCount
              }}
            />
          )}
          {(type === NotificationType.IDEA_PHASE_REACHED || type === NotificationType.IDEA_MILESTONE_REACHED) &&
            notificationCount === 1 && (
              <FormattedMessage
                id="anIdeaReached"
                defaultMessage="The idea <a>{ideaTitle}</a> from the challenge <b>{challengeTitle}</b> reached the next {target}"
                values={{
                  challengeTitle,
                  ideaTitle: removeTags(idea?.title || ''),
                  target,
                  a: (msg: string) => (isValid ? IdeaLink(msg, idea?.id) : msg),
                  b: (msg: string) => challengeLink(msg, challenge?.id)
                }}
              />
            )}
          {(type === NotificationType.IDEA_PHASE_REACHED || type === NotificationType.IDEA_MILESTONE_REACHED) &&
            notificationCount > 1 && (
              <FormattedMessage
                id="ideasReached"
                defaultMessage="Ideas from the challenge <b>{challengeTitle}</b> reached the next {target}"
                values={{
                  challengeTitle,
                  target,
                  b: (msg: string) => challengeLink(msg, challenge?.id)
                }}
              />
            )}
          {type === NotificationType.BLOG_POST_NEW && (
            <FormattedMessage
              id="blogHasNewPosts"
              defaultMessage="Check out {itemCount, plural, one {this new blog post} other {these new blog posts}}"
              values={{
                itemCount: notificationCount
              }}
            />
          )}
        </Styled.AlignItemsWrap>
      )}

      {isUserNotification && (
        <Styled.AlignItemsTop>
          {url ? <Styled.ProfileImage data-testid="UserProfileImage" src={url} /> : <Styled.NoProfileImage />}
          <Styled.AlignItemsWrap>
            {type === NotificationType.IDEA_NEW && (
              <FormattedMessage
                id="userContributedToChallenge"
                defaultMessage="<userLink>{userName}</userLink> contributed {itemCount, plural, one {an idea} other {ideas}} to the {challengeCount, plural, one {challenge <challengeLink>{challengeTitle}</challengeLink>} other {challenges}}"
                values={{
                  challengeTitle,
                  userLink: (msg: string) =>
                    UserLink(msg, author?.id, intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' })),
                  challengeLink: (msg: string) => challengeLink(msg, challenge?.id),
                  itemCount: notificationCount,
                  userName: author?.displayName,
                  challengeCount: notificationCount === 1 ? 1 : sameChallenge ? 1 : 2
                }}
              />
            )}
            {(type === NotificationType.IDEA_PHASE_REACHED || type === NotificationType.IDEA_MILESTONE_REACHED) && (
              <FormattedMessage
                id="userIdeaReached"
                defaultMessage="<a>{authorName}</a> {itemCount, plural, one {idea} other {ideas}} from the challenge <b>{challengeTitle}</b> reached the next {target}"
                values={{
                  challengeTitle,
                  authorName: author?.displayName,
                  target,
                  a: (msg: string) =>
                    UserLink(msg, author?.id, intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' })),
                  b: (msg: string) => challengeLink(msg, challenge?.id),
                  itemCount: notificationCount
                }}
              />
            )}
            {type === NotificationType.BLOG_POST_NEW && (
              <FormattedMessage
                id="userBlogHasNewPosts"
                defaultMessage="<userLink>{userName}</userLink> contributed to the blog"
                values={{
                  userLink: (msg: string) =>
                    UserLink(msg, author?.id, intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' })),
                  userName: author?.displayName
                }}
              />
            )}
            {type === NotificationType.IDEA_TAG && (
              <FormattedMessage
                id="taggedOnIdea"
                defaultMessage="<userLink>{userName}</userLink> has tagged you on an idea"
                values={{
                  userLink: (msg: string) =>
                    UserLink(msg, author?.id, intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' })),
                  userName: author?.displayName
                }}
              />
            )}
          </Styled.AlignItemsWrap>
        </Styled.AlignItemsTop>
      )}
    </>
  );
};
